/**
 * SettingsPage
 *
 * App settings: Account, Theme picker, Font size, Import/Export, Data management.
 * Theme system is a key feature - users can customize appearance.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '@hooks/useTheme';
import { supabase } from '@services/supabase';
import { useSyncStatus } from '@hooks/useSyncStatus';
import { useUpgrade } from '@hooks/useUpgrade';
import { useAuth } from '@services/auth';
import { Card, CardHeader, Button } from '@/components/shared';
import { db, exportData, clearAllData, Account } from '@services/db';
import { deleteAccount } from '@services/sync';
import { syncManager } from '@services/syncManager';
import { accountService } from '@services/accounts';
import { DeleteAccountModal } from '@/components/DeleteAccountModal';
import { SignOutWarningModal, SignOutScope } from '@/components/SignOutWarningModal';
import { UpgradeModalA } from '@/components/upgrade';
import { SubscriptionStatus } from '@/components/settings/SubscriptionStatus';
import { LanguageCard } from '@/components/settings/LanguageCard';
import { useLanguage } from '@/i18n';
import { formatDate as formatDateIntl } from '@/utils/format';
import './SettingsPage.css';

export default function SettingsPage() {
  const { t } = useLanguage();
  const {
    theme,
    themes,
    setTheme,
    fontSize,
    fontSizePresets,
    fontSizeLimits,
    setFontSize,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize,
  } = useTheme();
  const { user, signOut, signOutEverywhere } = useAuth();
  const syncStatus = useSyncStatus();
  const {
    isPro,
    showUpgradeModal,
    upgradeModalTrigger,
    openUpgradeModal,
    closeUpgradeModal,
    handleSelectPlan,
  } = useUpgrade();
  const [exporting, setExporting] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [signingOutEverywhere, setSigningOutEverywhere] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [signOutScope, setSignOutScope] = useState<SignOutScope | null>(null);
  const [searchParams] = useSearchParams();
  const [upgradePolling, setUpgradePolling] = useState(false);
  const pollingRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { session } = useAuth();

  // localStorage keys
  const PENDING_CHECKOUT_KEY = 'ninjacash_pending_checkout';  // New: checkoutId for instant verify
  const PENDING_UPGRADE_KEY = 'ninjacash_pending_upgrade';    // Legacy: boolean flag for polling

  // API base URL
  const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.ninjacash.app';

  // Stop polling and clean up
  const stopPolling = useCallback((clearStorage = true) => {
    pollingRef.current = false;
    setUpgradePolling(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (clearStorage) {
      localStorage.removeItem(PENDING_UPGRADE_KEY);
    }
  }, []);

  // Verify checkout via API (instant, ~1-2s instead of ~60s webhook wait)
  const MAX_VERIFY_RETRIES = 15; // ~30 seconds max with backoff

  const verifyCheckout = useCallback(async (checkoutId: string, attempt: number) => {
    setUpgradePolling(true);

    // SECURITY: Wait for auth token before calling API
    if (!session?.access_token) {
      console.log('[Settings] No auth token yet, waiting...');
      // Retry in 500ms - session should load shortly
      timeoutRef.current = setTimeout(() => verifyCheckout(checkoutId, attempt), 500);
      return;
    }

    // Max retries reached - fall back to Supabase polling
    if (attempt >= MAX_VERIFY_RETRIES) {
      console.log('[Settings] Max verify retries reached, falling back to polling');
      localStorage.removeItem(PENDING_CHECKOUT_KEY);
      startPolling();
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/checkout/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ checkout_id: checkoutId }),
      });

      const result = await response.json();
      console.log('[Settings] Verify attempt', attempt + 1, 'result:', result);

      if (result.verified) {
        // Success! Clear storage
        console.log('[Settings] Pro activated via instant verify!');
        localStorage.removeItem(PENDING_CHECKOUT_KEY);
        localStorage.removeItem(PENDING_UPGRADE_KEY);

        // Clear URL params to prevent loop
        const url = new URL(window.location.href);
        url.searchParams.delete('upgraded');
        window.history.replaceState({}, '', url.pathname + url.search);

        // CRITICAL: Refresh session to get new JWT with updated user_metadata
        // Server updated metadata, but client JWT still has old claims
        await supabase.auth.refreshSession();
        
        // Now reload to apply new Pro status everywhere
        window.location.reload();
        return;
      }

      if (result.status === 'pending') {
        // Payment still processing - retry with exponential backoff
        const delay = Math.min(2000 * Math.pow(1.2, attempt), 5000); // 2s -> 5s max
        timeoutRef.current = setTimeout(() => verifyCheckout(checkoutId, attempt + 1), delay);
        return;
      }

      if (result.status === 'rate_limited') {
        // Hit rate limit - wait longer, then retry
        timeoutRef.current = setTimeout(() => verifyCheckout(checkoutId, attempt + 1), 10000);
        return;
      }

      // Failed - fall back to Supabase polling (webhook might still work)
      console.log('[Settings] Verification failed, falling back to polling');
      localStorage.removeItem(PENDING_CHECKOUT_KEY);
      startPolling();

    } catch (err) {
      console.error('[Settings] Verify API error:', err);
      // API error - fall back to Supabase polling
      localStorage.removeItem(PENDING_CHECKOUT_KEY);
      startPolling();
    }
  }, [session?.access_token, API_BASE_URL]);

  // Start polling for subscription status (fallback when verify fails)
  const startPolling = useCallback(() => {
    if (pollingRef.current) return; // Already polling
    
    pollingRef.current = true;
    setUpgradePolling(true);
    localStorage.setItem(PENDING_UPGRADE_KEY, 'true');
    
    console.log('[Settings] Starting upgrade polling...');
    
    let attempts = 0;
    const maxAttempts = 60; // 60 attempts × 3s = 3 min (webhooks can take 2+ min)
    
    const poll = async () => {
      // Check if polling was cancelled
      if (!pollingRef.current) {
        console.log('[Settings] Polling cancelled');
        return;
      }
      
      try {
        // Must refresh session FIRST to get new JWT with updated claims
        const { error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          console.error('[Settings] refreshSession error:', refreshError);
          // Continue polling despite error
        }
        
        const { data, error } = await supabase.auth.getUser();
        if (error) {
          console.error('[Settings] getUser error:', error);
          // Continue polling despite error
        }
        
        const status = data.user?.user_metadata?.subscriptionStatus;
        // Debug: log full metadata on first attempt and every 10th
        if (attempts === 0 || attempts % 10 === 0) {
          console.log('[Settings] Poll metadata:', JSON.stringify(data.user?.user_metadata));
        }
        console.log('[Settings] Poll attempt', attempts + 1, 'status:', status);
        
        if (status === 'active' || status === 'lifetime' || status === 'trialing') {
          console.log('[Settings] Pro activated!');
          stopPolling(true);
          
          // Clear URL params BEFORE reload to prevent loop
          const url = new URL(window.location.href);
          url.searchParams.delete('upgraded');
          url.searchParams.delete('checkout_id');
          window.history.replaceState({}, '', url.pathname + url.search);
          
          // Force page reload to update all Pro-dependent UI
          window.location.reload();
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          timeoutRef.current = setTimeout(poll, 3000); // 3s to avoid Supabase rate limits
        } else {
          console.log('[Settings] Max poll attempts reached (3 min timeout)');
          stopPolling(true);
          
          // Clear URL params on timeout too
          const url = new URL(window.location.href);
          url.searchParams.delete('upgraded');
          url.searchParams.delete('checkout_id');
          window.history.replaceState({}, '', url.pathname + url.search);
        }
      } catch (err) {
        console.error('[Settings] Poll error:', err);
        attempts++;
        if (attempts < maxAttempts && pollingRef.current) {
          timeoutRef.current = setTimeout(poll, 3000);
        }
      }
    };
    
    // Start polling after a short delay (give webhook time to process)
    timeoutRef.current = setTimeout(poll, 1500);
  }, [stopPolling]);

  // Handle upgrade verification after returning from Polar checkout
  // Priority: 1) Instant verify via API, 2) Fall back to Supabase polling
  useEffect(() => {
    const upgraded = searchParams.get('upgraded');
    const pendingCheckoutStr = localStorage.getItem(PENDING_CHECKOUT_KEY);
    const pendingUpgrade = localStorage.getItem(PENDING_UPGRADE_KEY);
    const currentStatus = user?.user_metadata?.subscriptionStatus;
    const isAlreadyPro = currentStatus === 'active' || currentStatus === 'lifetime' || currentStatus === 'trialing';
    
    // Clear stale flags if user is already Pro
    if (isAlreadyPro) {
      if (pendingCheckoutStr) localStorage.removeItem(PENDING_CHECKOUT_KEY);
      if (pendingUpgrade) localStorage.removeItem(PENDING_UPGRADE_KEY);
      return;
    }
    
    // Already processing
    if (pollingRef.current) return;
    
    // Check for pending checkout (instant verify path)
    if (pendingCheckoutStr) {
      try {
        const { checkoutId, startedAt } = JSON.parse(pendingCheckoutStr);
        // Only verify if checkout started within last 10 minutes
        if (checkoutId && startedAt && (Date.now() - startedAt) < 10 * 60 * 1000) {
          console.log('[Settings] Found pending checkout, starting instant verify:', checkoutId);
          pollingRef.current = true;
          verifyCheckout(checkoutId, 0);
          return;
        } else {
          // Expired - clean up
          localStorage.removeItem(PENDING_CHECKOUT_KEY);
        }
      } catch {
        localStorage.removeItem(PENDING_CHECKOUT_KEY);
      }
    }
    
    // Fall back to polling if: URL has ?upgraded=true OR legacy pending flag
    if (upgraded === 'true' || pendingUpgrade) {
      console.log('[Settings] No checkoutId, falling back to polling...', { upgraded, pendingUpgrade });
      startPolling();
    }
  }, [searchParams, user?.user_metadata?.subscriptionStatus, startPolling, verifyCheckout]);

  // Cleanup on unmount - stop polling but keep localStorage flag for resume
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        console.log('[Settings] Component unmounting, pausing poll (will resume on return)');
        stopPolling(false); // Keep localStorage so polling resumes when user comes back
      }
    };
  }, [stopPolling]);

  // Handle cloud sync toggle
  const handleSyncToggle = useCallback(() => {
    const newValue = !syncStatus.autoSyncEnabled;
    syncManager.updateSettings({ autoSyncEnabled: newValue });
    console.log('[Settings] Cloud sync toggled:', newValue);
  }, [syncStatus.autoSyncEnabled]);

  // Data snapshot for the Export card: record counts + device storage +
  // last backup date. Gives the export button real context ("what am I
  // backing up, and when did I last do it") - key for an offline-first app.
  const LAST_EXPORT_KEY = 'ninjacash_last_export';
  const [dataSnapshot, setDataSnapshot] = useState<{
    accounts: number;
    transactions: number;
    categories: number;
    storage: string | null;
  } | null>(null);
  const [lastExportAt, setLastExportAt] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LAST_EXPORT_KEY);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [accountsN, transactionsN, categoriesN] = await Promise.all([
          db.accounts.filter((a) => a.isActive).count(),
          db.transactions.filter((t) => !t.deletedAt).count(),
          db.categories.filter((c) => c.isActive).count(),
        ]);
        let storage: string | null = null;
        if (navigator.storage?.estimate) {
          const { usage } = await navigator.storage.estimate();
          if (usage && usage > 0) {
            storage = usage >= 1048576
              ? `${(usage / 1048576).toFixed(1)} MB`
              : `${Math.max(1, Math.round(usage / 1024))} KB`;
          }
        }
        if (!cancelled) {
          setDataSnapshot({ accounts: accountsN, transactions: transactionsN, categories: categoriesN, storage });
        }
      } catch (err) {
        console.error('[Settings] Data snapshot failed:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Hidden accounts state
  const [hiddenAccounts, setHiddenAccounts] = useState<Account[]>([]);
  const [loadingHidden, setLoadingHidden] = useState(true);
  const [unhidingId, setUnhidingId] = useState<string | null>(null);

  // Load hidden accounts
  useEffect(() => {
    const loadHiddenAccounts = async () => {
      try {
        const hidden = await accountService.getHidden();
        setHiddenAccounts(hidden);
      } catch (err) {
        console.error('[Settings] Failed to load hidden accounts:', err);
      } finally {
        setLoadingHidden(false);
      }
    };
    loadHiddenAccounts();
  }, []);

  // Handle unhide account
  const handleUnhideAccount = useCallback(async (id: string) => {
    setUnhidingId(id);
    try {
      await accountService.activate(id);
      // Remove from local list
      setHiddenAccounts(prev => prev.filter(a => a.id !== id));
      console.log('[Settings] Account unhidden:', id);
    } catch (err) {
      console.error('[Settings] Failed to unhide account:', err);
    } finally {
      setUnhidingId(null);
    }
  }, []);

  // Handle sign out (this browser only)
  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    const { error } = await signOut();
    if (error) {
      console.error('[Settings] Sign out error:', error.message);
    }
    setSigningOut(false);
    // Page will redirect to login automatically via auth state change
  }, [signOut]);

  // Handle sign out from all devices
  const handleSignOutEverywhere = useCallback(async () => {
    setSigningOutEverywhere(true);
    const { error } = await signOutEverywhere();
    if (error) {
      console.error('[Settings] Sign out everywhere error:', error.message);
    }
    setSigningOutEverywhere(false);
    // Page will redirect to login automatically via auth state change
  }, [signOutEverywhere]);

  // Request sign out - shows warning modal for free users, immediate for Pro
  const requestSignOut = useCallback((scope: SignOutScope) => {
    if (isPro) {
      // Pro users have cloud sync - safe to sign out immediately
      if (scope === 'local') {
        handleSignOut();
      } else {
        handleSignOutEverywhere();
      }
    } else {
      // Free users - show warning modal (data will be lost!)
      setSignOutScope(scope);
    }
  }, [isPro, handleSignOut, handleSignOutEverywhere]);

  // Confirm sign out from warning modal (free users only)
  const confirmSignOut = useCallback(async () => {
    if (signOutScope === 'local') {
      await handleSignOut();
    } else if (signOutScope === 'everywhere') {
      await handleSignOutEverywhere();
    }
    setSignOutScope(null);
  }, [signOutScope, handleSignOut, handleSignOutEverywhere]);

  // Close sign out warning modal
  const closeSignOutWarning = useCallback(() => {
    setSignOutScope(null);
  }, []);

  // Handle theme change
  const handleThemeChange = useCallback(
    (themeId: string) => {
      setTheme(themeId);
    },
    [setTheme]
  );

  // Handle export
  const handleExport = useCallback(async () => {
    setExporting(true);

    try {
      const data = await exportData();
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `ninjacash-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Remember when the last backup was downloaded (per device)
      try {
        const now = new Date().toISOString();
        localStorage.setItem(LAST_EXPORT_KEY, now);
        setLastExportAt(now);
      } catch {
        // localStorage unavailable - skip the reminder feature
      }

      console.log('[Settings] Export complete');
    } catch (err) {
      console.error('[Settings] Export error:', err);
    } finally {
      setExporting(false);
    }
  }, []);

  // Handle clear all data
  const handleClearData = useCallback(async () => {
    setClearing(true);

    try {
      await clearAllData();
      console.log('[Settings] Data cleared - reloading page');
      // Force page reload to reset all React state
      window.location.reload();
    } catch (err) {
      console.error('[Settings] Clear error:', err);
      setClearing(false);
    }
  }, []);

  // Handle account deletion
  const handleDeleteAccount = useCallback(async () => {
    setDeletingAccount(true);

    try {
      const result = await deleteAccount();

      console.log('[Settings] Account deletion result:', result);

      // Check if deletion was successful
      if (!result.success) {
        // Complete failure - neither local nor server cleared
        throw new Error(result.error || 'Account deletion failed');
      }

      // Partial success - local cleared but server might have failed
      if (!result.serverCleared && result.error) {
        console.warn('[Settings] Server data not deleted:', result.error);
        // Still proceed - local data is cleared, user will be signed out
        // Server data will be isolated by userId and inaccessible after sign out
      }

      // Sign out from Supabase
      const { error: signOutError } = await signOut();
      if (signOutError) {
        console.error('[Settings] Sign out error after deletion:', signOutError.message);
        // Still continue - data is deleted, just sign out failed
      }

      // Redirect will happen automatically via auth state change
    } catch (err) {
      console.error('[Settings] Delete account error:', err);
      // Ensure state is reset on error
      setDeletingAccount(false);
      setShowDeleteAccountModal(false);

      // Show error to user (could add a toast notification here)
      alert(t('settings.delete_account.failed_with', {
        message: err instanceof Error ? err.message : t('settings.delete_account.unknown_error'),
      }));
    }
  }, [signOut]);

  return (
    <div className="settings-page">
      {/* Upgrade polling indicator */}
      {upgradePolling && (
        <div className="upgrade-polling-banner">
          <div className="upgrade-polling-spinner" />
          <div className="upgrade-polling-content">
            <span className="upgrade-polling-title">🥷 Activating Ninja Pro...</span>
            <span className="upgrade-polling-subtitle">Confirming your upgrade now. This page refreshes automatically — no action needed.</span>
          </div>
        </div>
      )}

      {/* Account Section */}
      <section className="settings-section">
        <h2 className="section-title">{t('settings.section.account')}</h2>
        <Card className="settings-account-card">
          <div className="settings-account-info">
            {user?.user_metadata?.avatar_url && (
              <img
                src={user.user_metadata.avatar_url}
                alt=""
                className="settings-account-avatar"
              />
            )}
            <div className="settings-account-details">
              <div className="settings-account-name">
                {user?.user_metadata?.full_name || user?.email || t('settings.account.user_fallback')}
              </div>
              <div className="settings-account-email">{user?.email}</div>
            </div>
          </div>
          <div className="signout-buttons">
            <Button
              variant="secondary"
              onClick={() => requestSignOut('local')}
              disabled={signingOut || signingOutEverywhere}
            >
              {signingOut ? t('settings.account.signing_out') : t('settings.account.sign_out')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => requestSignOut('everywhere')}
              disabled={signingOut || signingOutEverywhere}
            >
              {signingOutEverywhere ? t('settings.account.signing_out') : t('settings.account.sign_out_everywhere')}
            </Button>
          </div>
        </Card>
      </section>

      {/* Subscription Management Section (for paying users) */}
      {user && isPro && (
        <section className="settings-section">
          <h2 className="section-title">{t('settings.section.subscription')}</h2>
          <SubscriptionStatus 
            onUpgrade={() => {
              // Open upgrade modal - will show lifetime option
              openUpgradeModal('sync-toggle');
            }} 
          />
        </section>
      )}

      {/* Cloud Sync Section */}
      {user && (
        <section className="settings-section">
          <h2 className="section-title">{t('settings.section.sync')}</h2>
          <Card className="sync-card">
            {isPro ? (
              /* Pro users: Show sync toggle */
              <>
                <div className="sync-toggle-row">
                  <div className="sync-toggle-info">
                    <div className="sync-toggle-title">
                      {t('settings.sync.title')}
                      <span className="pro-badge">PRO</span>
                    </div>
                    <div className="sync-toggle-subtitle">
                      {syncStatus.autoSyncEnabled
                        ? syncStatus.connectionStatus === 'connected'
                          ? t('settings.sync.on')
                          : syncStatus.connectionStatus === 'checking'
                          ? t('settings.sync.checking')
                          : t('settings.sync.offline')
                        : t('settings.sync.disabled')}
                    </div>
                  </div>
                  <button
                    className={`sync-toggle-switch ${syncStatus.autoSyncEnabled ? 'active' : ''}`}
                    onClick={handleSyncToggle}
                    role="switch"
                    aria-checked={syncStatus.autoSyncEnabled}
                    aria-label={t('settings.sync.toggle_aria')}
                  >
                    <span className="sync-toggle-thumb" />
                  </button>
                </div>
                {syncStatus.autoSyncEnabled && syncStatus.lastSyncAt && (
                  <div className="sync-last-sync">
                    {t('settings.sync.last_synced', {
                      datetime: formatDateIntl(new Date(syncStatus.lastSyncAt), {
                        dateStyle: 'short',
                        timeStyle: 'medium',
                      }),
                    })}
                  </div>
                )}
              </>
            ) : (
              /* Free users: Show upgrade prompt */
              <div className="sync-upgrade-prompt">
                <div className="sync-upgrade-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
                    <path d="M22 10v4" />
                    <path d="M20 12h4" />
                  </svg>
                </div>
                <div className="sync-upgrade-content">
                  <div className="sync-upgrade-title">{t('settings.sync.title')}</div>
                  <div className="sync-upgrade-subtitle">
                    {t('settings.sync.upgrade_subtitle')}
                  </div>
                </div>
                <Button
                  variant="primary"
                  onClick={() => openUpgradeModal('sync-toggle')}
                  className="sync-upgrade-button"
                >
                  {t('settings.sync.upgrade')}
                </Button>
                <Link to="/pricing" className="sync-pricing-link">
                  {t('settings.sync.pricing_link')}
                </Link>
              </div>
            )}
          </Card>
        </section>
      )}

      {/* Help & Support Section */}
      <section className="settings-section">
        <h2 className="section-title">{t('settings.section.help')}</h2>
        <Card className="support-card">
          <a
            href="https://github.com/TakashiKyoto/ninjacash/issues"
            target="_blank"
            rel="noopener noreferrer"
            className="support-link"
          >
            <div className="support-link-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
            </div>
            <div className="support-link-content">
              <span className="support-link-title">{t('settings.help.report_title')}</span>
              <span className="support-link-subtitle">{t('settings.help.report_subtitle')}</span>
            </div>
            <svg className="support-link-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
          <a
            href="mailto:takashi.ncdev@ninjacash.app"
            className="support-link"
          >
            <div className="support-link-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <div className="support-link-content">
              <span className="support-link-title">{t('settings.help.email_title')}</span>
              <span className="support-link-subtitle">takashi.ncdev@ninjacash.app</span>
            </div>
            <svg className="support-link-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
        </Card>
      </section>

      {/* Theme Section */}
      <section className="settings-section">
        <h2 className="section-title">{t('settings.section.appearance')}</h2>

        {/* Language picker (F5 Phase 1) - native names, instant switch */}
        <LanguageCard />

        <Card className="theme-card">
          <CardHeader
            title={t('settings.theme.title')}
            subtitle={t('settings.theme.current', { name: theme.name })}
          />
          <div className="theme-grid">
            {themes.map((th) => (
              <button
                key={th.id}
                className={`theme-option ${th.id === theme.id ? 'active' : ''}`}
                onClick={() => handleThemeChange(th.id)}
                title={th.description}
              >
                <div
                  className="theme-preview"
                  style={{
                    background: th.colors.bgSurface,
                    borderColor: th.colors.borderDefault,
                  }}
                >
                  <div
                    className="theme-preview-accent"
                    style={{ background: th.colors.accent }}
                  />
                  <div
                    className="theme-preview-text"
                    style={{ background: th.colors.textPrimary }}
                  />
                </div>
                <span className="theme-name">{th.name}</span>
              </button>
            ))}
          </div>
        </Card>

        {/* Font Size Card */}
        <Card className="font-size-card">
          <CardHeader
            title={t('settings.text_size.title')}
            subtitle={t('settings.text_size.current', { size: fontSize })}
          />

          {/* Preset buttons */}
          <div className="font-size-presets">
            {fontSizePresets.map((preset) => (
              <button
                key={preset.id}
                className={`preset-btn ${fontSize === preset.size ? 'active' : ''}`}
                onClick={() => setFontSize(preset.size)}
              >
                {preset.name}
              </button>
            ))}
          </div>

          {/* Fine-tune controls */}
          <div className="font-size-controls">
            <button
              className="size-btn"
              onClick={decreaseFontSize}
              disabled={fontSize <= fontSizeLimits.min}
              aria-label={t('settings.text_size.decrease_aria')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>

            <div className="size-display">
              <span className="size-value">{fontSize}</span>
              <span className="size-unit">px</span>
            </div>

            <button
              className="size-btn"
              onClick={increaseFontSize}
              disabled={fontSize >= fontSizeLimits.max}
              aria-label={t('settings.text_size.increase_aria')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
          </div>

          {/* Reset button */}
          {fontSize !== fontSizeLimits.default && (
            <button className="reset-btn" onClick={resetFontSize}>
              {t('settings.text_size.reset', { size: fontSizeLimits.default })}
            </button>
          )}
        </Card>
      </section>

      {/* Import Section - Link to dedicated Import page */}
      <section className="settings-section">
        <h2 className="section-title">{t('settings.section.import')}</h2>
        <Card>
          <CardHeader
            title={t('settings.import.title')}
            subtitle={t('settings.import.subtitle')}
          />
          <p className="import-description">
            {t('settings.import.description')}
          </p>
          <Link to="/import" className="import-link-button">
            <Button variant="primary">
              {t('settings.import.go')}
            </Button>
          </Link>
        </Card>
      </section>

      {/* Export Section */}
      <section className="settings-section">
        <h2 className="section-title">{t('settings.section.export')}</h2>
        <Card>
          <CardHeader
            title={t('settings.export.title')}
            subtitle={t('settings.export.subtitle')}
          />
          {dataSnapshot && (
            <p className="export-snapshot">
              {t('settings.export.stats', {
                accounts: dataSnapshot.accounts,
                transactions: dataSnapshot.transactions,
                categories: dataSnapshot.categories,
              })}
              {dataSnapshot.storage
                ? ` · ${t('settings.export.storage', { size: dataSnapshot.storage })}`
                : ''}
            </p>
          )}
          <Button
            variant="secondary"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? t('settings.export.exporting') : t('settings.export.download')}
          </Button>
          <p className={`export-last-backup ${!lastExportAt ? 'never' : ''}`}>
            {lastExportAt
              ? t('settings.export.last_backup', {
                  when: formatDateIntl(new Date(lastExportAt), { dateStyle: 'medium' }),
                })
              : t('settings.export.last_backup_never')}
          </p>
        </Card>
      </section>

      {/* Hidden Accounts Section */}
      {!loadingHidden && hiddenAccounts.length > 0 && (
        <section className="settings-section">
          <h2 className="section-title">{t('settings.section.hidden')}</h2>
          <Card>
            <CardHeader
              title={t('settings.hidden.title')}
              subtitle={t(
                hiddenAccounts.length === 1 ? 'settings.hidden.subtitle_one' : 'settings.hidden.subtitle_other',
                { count: hiddenAccounts.length }
              )}
            />
            <div className="hidden-accounts-list">
              {hiddenAccounts.map((account) => (
                <div key={account.id} className="hidden-account-item">
                  <div className="hidden-account-info">
                    <span className="hidden-account-name">{account.name}</span>
                    {account.fullName && (
                      <span className="hidden-account-fullname">{account.fullName}</span>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleUnhideAccount(account.id!)}
                    disabled={unhidingId === account.id}
                  >
                    {unhidingId === account.id ? t('settings.hidden.restoring') : t('settings.hidden.unhide')}
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        </section>
      )}

      {/* Danger Zone */}
      <section className="settings-section danger-zone">
        <h2 className="section-title">{t('settings.section.danger')}</h2>
        <Card>
          <CardHeader
            title={t('settings.danger.title')}
            subtitle={t('settings.danger.subtitle')}
          />
          {!showClearConfirm ? (
            <Button
              variant="danger"
              onClick={() => setShowClearConfirm(true)}
            >
              {t('settings.danger.title')}
            </Button>
          ) : (
            <div className="confirm-clear">
              <p>{t('settings.danger.confirm')}</p>
              <div className="confirm-buttons">
                <Button
                  variant="ghost"
                  onClick={() => setShowClearConfirm(false)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="danger"
                  onClick={handleClearData}
                  disabled={clearing}
                >
                  {clearing ? t('settings.danger.clearing') : t('settings.danger.yes_delete_everything')}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </section>

      {/* Delete Account Section - Only show if user is authenticated */}
      {user && (
        <section className="settings-section delete-account-section">
          <h2 className="section-title">{t('settings.section.delete_account')}</h2>
          <Card>
            <CardHeader
              title={t('settings.delete_account.title')}
              subtitle={t('settings.delete_account.subtitle')}
            />
            <p className="delete-account-description">
              {t('settings.delete_account.description')}
            </p>
            <Button
              variant="danger"
              onClick={() => setShowDeleteAccountModal(true)}
              disabled={deletingAccount}
            >
              {t('settings.delete_account.button')}
            </Button>
          </Card>
        </section>
      )}

      {/* Legal & App Info */}
      <section className="settings-section">
        <h2 className="section-title">{t('settings.section.legal')}</h2>
        <Card className="legal-links-card">
          <div className="legal-links">
            <Link to="/privacy" className="legal-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>{t('settings.legal.privacy')}</span>
              <svg className="legal-link-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </Link>
            <Link to="/terms" className="legal-link">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              <span>{t('settings.legal.terms')}</span>
              <svg className="legal-link-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </Link>
          </div>
        </Card>
        <p className="financial-disclaimer">
          {t('settings.legal.disclaimer')}
        </p>
        <div className="app-info">
          <div className="app-name">NinjaCash</div>
          <div className="app-version">
            {t('settings.app.version_build', {
              version: '1.0.0',
              date: formatDateIntl(new Date(__BUILD_TS__), { dateStyle: 'medium' }),
            })}
          </div>
          <div className="app-copyright">All Rights Reserved</div>
        </div>
      </section>

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteAccountModal}
        onClose={() => setShowDeleteAccountModal(false)}
        onConfirm={handleDeleteAccount}
        isDeleting={deletingAccount}
      />

      {/* Upgrade Modal (for free users) */}
      <UpgradeModalA
        isOpen={showUpgradeModal}
        onClose={closeUpgradeModal}
        onSelectPlan={handleSelectPlan}
        triggerSource={upgradeModalTrigger}
      />

      {/* Sign Out Warning Modal (for free users - data loss warning) */}
      <SignOutWarningModal
        isOpen={signOutScope !== null}
        scope={signOutScope || 'local'}
        onClose={closeSignOutWarning}
        onExport={handleExport}
        onConfirm={confirmSignOut}
        isSigningOut={signingOut || signingOutEverywhere}
        isExporting={exporting}
      />
    </div>
  );
}
